import React, { useState, useEffect, useMemo } from 'react';
import { AiOutlineKey, AiOutlineCopy, AiOutlineDelete, AiOutlinePlus, AiOutlineDown, AiOutlineRight } from 'react-icons/ai';
import { apiUrl, API_BASE_URL } from '../config/api';
import { useAppSettings, setFeature } from '../lib/appSettings';
import './IntegrationsPage.css';

// Who may call an endpoint — mirrors the backend's access levels.
const ACCESS = {
  public: { label: 'Public', hint: 'No credentials needed' },
  apikey: { label: 'API key', hint: 'An API key or a signed-in user' },
  session: { label: 'Sign-in only', hint: 'A signed-in user — API keys are refused' },
  staff: { label: 'Admin or manager', hint: 'A signed-in admin or manager — API keys are refused' },
  admin: { label: 'Admin sign-in', hint: 'A signed-in admin — API keys never have admin rights' },
};

const EXAMPLE_VALUE = { integer: 1, number: 1, boolean: true, file: '@photo.jpg' };

function exampleFor(field) {
  if (field.type in EXAMPLE_VALUE) return EXAMPLE_VALUE[field.type];
  if (/address/i.test(field.name)) return '123 Main St, Virginia Beach, VA';
  if (/name/i.test(field.name)) return 'John Smith';
  if (/date/i.test(field.name)) return '2026-09-27';
  if (/id$/i.test(field.name)) return 1;
  return '...';
}

// A ready-to-run curl command for an endpoint.
function curlFor(endpoint, baseUrl) {
  let path = endpoint.path;
  endpoint.pathParams.forEach((p) => { path = path.replace(`{${p.name}}`, '1'); });
  const required = endpoint.queryParams.filter((q) => q.required);
  const query = (required.length ? required : endpoint.queryParams.slice(0, 1))
    .map((q) => `${q.name}=${encodeURIComponent(exampleFor(q))}`).join('&');
  const lines = [`curl -X ${endpoint.method} "${baseUrl}${path}${query ? `?${query}` : ''}"`];

  if (endpoint.access === 'apikey') lines.push('  -H "X-API-Key: $CAPTURIFY_API_KEY"');
  else if (endpoint.access !== 'public') lines.push('  -H "Authorization: Bearer $CAPTURIFY_TOKEN"');

  if (endpoint.bodyType === 'application/json') {
    const body = Object.fromEntries(endpoint.body.map((f) => [f.name, exampleFor(f)]));
    lines.push('  -H "Content-Type: application/json"');
    lines.push(`  -d '${JSON.stringify(body)}'`);
  } else if (endpoint.bodyType === 'multipart/form-data') {
    endpoint.body.filter((f) => f.required || f.type === 'file' || /clientId/.test(f.name))
      .forEach((f) => lines.push(`  -F "${f.name}=${exampleFor(f)}"`));
  }
  return lines.join(' \\\n');
}

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" className="ig-copy-btn"
      onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } }}>
      <AiOutlineCopy size={13} /> {copied ? 'Copied' : label}
    </button>
  );
};

const CodeBlock = ({ code }) => (
  <div className="ig-code-wrap">
    <pre className="ig-code">{code}</pre>
    <CopyButton text={code} />
  </div>
);

const FieldTable = ({ title, fields }) => (
  <div className="ig-fields">
    <p className="ig-fields-title">{title}</p>
    <table className="ig-table">
      <tbody>
        {fields.map((f) => (
          <tr key={f.name}>
            <td><code>{f.name}</code></td>
            <td className="ig-type">{f.type}</td>
            <td className="ig-req">{f.required ? 'required' : 'optional'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── API keys tab ─────────────────────────────────────────────────────────────
const ApiKeysTab = ({ token, baseUrl }) => {
  const [keys, setKeys] = useState([]);
  const [summary, setSummary] = useState(null); // main admin only: every admin's keys
  const [showRevoked, setShowRevoked] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState(null); // full key — shown once
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await fetch(apiUrl('/api/auth/api-keys'), { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
        setSummary(data.summary || null);
      }
    } catch { /* silent */ } finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/api-keys'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.detail || 'Could not create the API key'); return; }
      setCreatedKey(data.key); setShowNameInput(false); setName('');
      await load();
    } catch { setError('Failed to connect to server.'); }
    finally { setLoading(false); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using this key will stop working immediately.`)) return;
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/auth/api-keys/${k.id}`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { setError('Could not revoke the API key'); return; }
      await load();
    } catch { setError('Failed to connect to server.'); }
  };

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  const example = `curl -X POST "${baseUrl}/api/scraper/create-client" \\
  -H "X-API-Key: ${createdKey || '$CAPTURIFY_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"clientName": "John Smith", "clientAddress": "123 Main St, Virginia Beach, VA"}'`;

  return (
    <>
      <div className="ig-card">
        <div className="ig-card-head">
          <div>
            <h2 className="ig-card-title">API keys</h2>
            <p className="ig-card-sub">
              Let automations call Capturify without signing in. A key acts as the admin who created it, but never with admin rights.
              {summary ? ' You can see and revoke every admin\'s keys.' : ' You see the keys you created.'}
            </p>
          </div>
          {!showNameInput && (
            <button type="button" className="ig-primary-btn" onClick={() => { setShowNameInput(true); setError(''); }} disabled={loading}>
              <AiOutlinePlus size={14} /> Create key
            </button>
          )}
        </div>

        {showNameInput && (
          <div className="ig-create-row">
            <input type="text" className="ig-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="What it's for (e.g. GHL automation)" maxLength={60} disabled={loading} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) create(); }} />
            <button type="button" className="ig-secondary-btn" onClick={() => { setShowNameInput(false); setName(''); }} disabled={loading}>Cancel</button>
            <button type="button" className="ig-primary-btn" onClick={create} disabled={loading || !name.trim()}>
              {loading ? 'Creating…' : 'Create'}
            </button>
          </div>
        )}

        {createdKey && (
          <div className="ig-key-reveal">
            <p className="ig-key-reveal-title">Copy this key now — it won't be shown again.</p>
            <div className="ig-key-reveal-row">
              <code className="ig-key-value">{createdKey}</code>
              <CopyButton text={createdKey} label="Copy key" />
            </div>
            <button type="button" className="ig-link-btn" onClick={() => setCreatedKey(null)}>I've saved it</button>
          </div>
        )}

        {error && <p className="ig-error">{error}</p>}

        {activeKeys.length > 0 ? (
          <ul className="ig-key-list">
            {activeKeys.map((k) => (
              <li key={k.id} className="ig-key-row">
                <span className="ig-key-icon"><AiOutlineKey size={15} /></span>
                <div className="ig-key-info">
                  <span className="ig-key-name">{k.name}</span>
                  <span className="ig-key-meta">
                    <code>{k.prefix}…</code> · created by {k.ownerEmail} · {k.lastUsedAt
                      ? `last used ${new Date(k.lastUsedAt).toLocaleString()}`
                      : `created ${new Date(k.createdAt).toLocaleDateString()}, never used`}
                  </span>
                </div>
                <button type="button" className="ig-revoke-btn" onClick={() => revoke(k)} aria-label={`Revoke ${k.name}`}>
                  <AiOutlineDelete size={15} /> <span>Revoke</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          loaded && !createdKey && <p className="ig-empty">No active API keys.</p>
        )}

        {revokedKeys.length > 0 && (
          <>
            <button type="button" className="ig-link-btn" onClick={() => setShowRevoked((v) => !v)}>
              {showRevoked ? 'Hide' : 'Show'} {revokedKeys.length} revoked key{revokedKeys.length !== 1 ? 's' : ''}
            </button>
            {showRevoked && (
              <ul className="ig-key-list ig-key-list--revoked">
                {revokedKeys.map((k) => (
                  <li key={k.id} className="ig-key-row">
                    <span className="ig-key-icon"><AiOutlineKey size={15} /></span>
                    <div className="ig-key-info">
                      <span className="ig-key-name">{k.name} <span className="ig-revoked-tag">Revoked</span></span>
                      <span className="ig-key-meta">
                        <code>{k.prefix}…</code> · created by {k.ownerEmail} on {new Date(k.createdAt).toLocaleDateString()}
                        {' '}· revoked {new Date(k.revokedAt).toLocaleDateString()}{k.revokedByEmail ? ` by ${k.revokedByEmail}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {summary && (
        <div className="ig-card">
          <h2 className="ig-card-title">All API keys</h2>
          <p className="ig-card-sub">Every key created by any administrator. Only you can see this.</p>
          <div className="ig-stats">
            <div className="ig-stat"><span className="ig-stat-value">{summary.total}</span><span className="ig-stat-label">created</span></div>
            <div className="ig-stat"><span className="ig-stat-value">{summary.active}</span><span className="ig-stat-label">active</span></div>
            <div className="ig-stat"><span className="ig-stat-value">{summary.revoked}</span><span className="ig-stat-label">revoked</span></div>
          </div>
          {summary.byAdmin.length > 0 ? (
            <table className="ig-table ig-admin-table">
              <thead>
                <tr><th>Admin</th><th>Created</th><th>Active</th><th>Last created</th></tr>
              </thead>
              <tbody>
                {summary.byAdmin.map((a) => (
                  <tr key={a.email}>
                    <td><span className="ig-admin-name">{a.name || a.email}</span>{a.name && <span className="ig-admin-email">{a.email}</span>}</td>
                    <td>{a.total}</td>
                    <td>{a.active}</td>
                    <td>{a.lastCreatedAt ? new Date(a.lastCreatedAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="ig-empty">No administrator has created an API key yet.</p>
          )}
        </div>
      )}

      <div className="ig-card">
        <h2 className="ig-card-title">Quick start</h2>
        <p className="ig-card-sub">Create a project with an API key. Send the key in the <code>X-API-Key</code> header (or <code>Authorization: Bearer</code>).</p>
        <CodeBlock code={example} />
        <p className="ig-card-sub">Returns <code>{'{"clientId": 123, "created": true}'}</code> — or <code>"created": false</code> with the existing project if one with the same name and address already exists.</p>
      </div>
    </>
  );
};

// ── API documentation tab ───────────────────────────────────────────────────
const DocsTab = ({ token, baseUrl }) => {
  const [reference, setReference] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/integrations/reference'), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        setReference(await res.json());
      } catch { setError('Could not load the API reference.'); }
    })();
  }, [token]);

  const groups = useMemo(() => {
    if (!reference) return [];
    const q = filter.trim().toLowerCase();
    return reference.groups
      .map((g) => ({ ...g, endpoints: g.endpoints.filter((e) => !q || `${e.method} ${e.path} ${e.summary} ${g.name}`.toLowerCase().includes(q)) }))
      .filter((g) => g.endpoints.length);
  }, [reference, filter]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <>
      <div className="ig-card">
        <h2 className="ig-card-title">Getting started</h2>
        <dl className="ig-dl">
          <dt>Base URL</dt>
          <dd>
            <code>{baseUrl}</code>
            {baseUrl !== window.location.origin && (
              <> — the API has its own domain. The older <code>{window.location.origin}/api/…</code> address still
                works for existing integrations, but use this one for anything new.</>
            )}
          </dd>
          <dt>Interactive docs</dt>
          <dd>
            Try every endpoint in the browser at <a href={`${baseUrl}/docs`} target="_blank" rel="noreferrer"><code>{baseUrl}/docs</code></a>
            {' '}(Swagger — click <em>Authorize</em> and paste an API key), or read <a href={`${baseUrl}/redoc`} target="_blank" rel="noreferrer"><code>/redoc</code></a>.
            The machine-readable schema is at <code>{baseUrl}/openapi.json</code> (import it into Postman).
          </dd>
          <dt>Authentication</dt>
          <dd>
            Send an API key as <code>X-API-Key: cap_…</code> or <code>Authorization: Bearer cap_…</code>. Create keys on the API keys tab.
            Endpoints marked <em>Sign-in only</em> or <em>Admin sign-in</em> need a token from <code>POST /api/auth/login</code> instead (valid 24 hours).
          </dd>
          <dt>Format</dt>
          <dd>JSON in and out, except photo uploads, which are <code>multipart/form-data</code>.</dd>
          <dt>Photo links</dt>
          <dd>Photo URLs in responses (<code>imageUrl</code>, <code>fullImageUrl</code>) are signed and expire after 1 hour — fetch them again rather than storing them.</dd>
          <dt>Duplicates</dt>
          <dd><code>POST /api/scraper/create-client</code> returns the existing project (<code>"created": false</code>) when one with exactly the same name and address exists, so retrying is safe.</dd>
          <dt>Browser access</dt>
          <dd>Calls from a web page are only allowed from the Capturify app itself. Server-to-server calls (automations, scripts, Postman) work from anywhere.</dd>
          <dt>Rate limits</dt>
          <dd>
            Per API key: 300 requests/minute (bursts of 60). Per client IP without a key: 600/minute (bursts of 120).
            Sign-in: 10 attempts/minute per IP and 20 failed attempts/hour per account. Public timeline uploads: 10/minute per IP.
            Over a limit you get <code>429 Too Many Requests</code> with a <code>Retry-After</code> header (seconds); every response
            shows <code>X-RateLimit-Limit</code> and <code>X-RateLimit-Remaining</code>.
          </dd>
          <dt>Errors</dt>
          <dd>
            <code>400</code> invalid input · <code>401</code> missing, wrong or revoked key/token · <code>403</code> not allowed for this key/user · <code>404</code> not found · <code>429</code> rate limited.
            The body is <code>{'{"detail": "…"}'}</code>.
          </dd>
        </dl>
        <div className="ig-legend">
          {Object.entries(ACCESS).map(([id, a]) => (
            <span key={id} className="ig-legend-item"><span className={`ig-access ig-access--${id}`}>{a.label}</span> {a.hint}</span>
          ))}
        </div>
      </div>

      <div className="ig-card">
        <div className="ig-card-head">
          <div>
            <h2 className="ig-card-title">API reference</h2>
            <p className="ig-card-sub">{reference ? `${reference.count} endpoints, generated from the live server.` : 'Loading…'}</p>
          </div>
        </div>
        <input type="text" className="ig-input ig-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter endpoints (e.g. project, upload, GET)…" />
        {error && <p className="ig-error">{error}</p>}

        {groups.map((g) => (
          <section key={g.id} className="ig-group">
            <h3 className="ig-group-title">{g.name}</h3>
            <ul className="ig-endpoints">
              {g.endpoints.map((e) => {
                const id = `${e.method} ${e.path}`;
                const isOpen = !!open[id];
                return (
                  <li key={id} className={`ig-endpoint${isOpen ? ' ig-endpoint--open' : ''}`}>
                    <button type="button" className="ig-endpoint-head" onClick={() => toggle(id)} aria-expanded={isOpen}>
                      {isOpen ? <AiOutlineDown size={12} /> : <AiOutlineRight size={12} />}
                      <span className={`ig-method ig-method--${e.method.toLowerCase()}`}>{e.method}</span>
                      <code className="ig-path">{e.path}</code>
                      <span className={`ig-access ig-access--${e.access}`}>{ACCESS[e.access].label}</span>
                    </button>
                    <p className="ig-summary">{e.summary}</p>
                    {isOpen && (
                      <div className="ig-endpoint-body">
                        {e.pathParams.length > 0 && <FieldTable title="Path parameters" fields={e.pathParams} />}
                        {e.queryParams.length > 0 && <FieldTable title="Query parameters" fields={e.queryParams} />}
                        {e.body.length > 0 && <FieldTable title={`Body (${e.bodyType})`} fields={e.body} />}
                        <p className="ig-fields-title">Example</p>
                        <CodeBlock code={curlFor(e, baseUrl)} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {reference && groups.length === 0 && <p className="ig-empty">No endpoints match “{filter}”.</p>}
      </div>
    </>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
// App-wide switches. Shown only to the main administrator; the server refuses
// anyone else, so hiding the card is not the protection.
const FEATURE_ROWS = [
  {
    feature: 'video',
    field: 'videoEnabled',
    name: 'Video recording & uploads',
    desc: 'Lets technicians record videos in the camera and upload videos (up to 2 GB) to projects. Videos already uploaded stay visible either way.',
  },
  {
    feature: 'nearby-jobs',
    field: 'nearbyJobsEnabled',
    name: 'Jobs near me',
    desc: "Shows booked GoHighLevel jobs closest to the technician's location on the Home screen.",
  },
];

const AppFeaturesCard = () => {
  const settings = useAppSettings({ refresh: true });
  const [savingFeature, setSavingFeature] = useState(null);
  const [error, setError] = useState('');

  const toggle = async (row) => {
    setSavingFeature(row.feature);
    setError('');
    try {
      await setFeature(row.feature, !settings[row.field]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingFeature(null);
    }
  };

  return (
    <div className="ig-card ig-features">
      <h2 className="ig-card-title">App features</h2>
      <p className="ig-card-sub">Turn features on or off for everyone. Only you can see and change these.</p>
      {FEATURE_ROWS.map((row) => {
        const on = !!settings[row.field];
        const change = settings.changes?.[row.field];
        return (
          <div key={row.feature} className="ig-feature-row">
            <div className="ig-feature-text">
              <span className="ig-feature-name">
                {row.name}
                <span className={`ig-feature-pill${on ? ' ig-feature-pill--on' : ''}`}>
                  {!settings.loaded ? '…' : on ? 'On' : 'Off'}
                </span>
              </span>
              <span className="ig-feature-desc">{row.desc}</span>
              {change && (
                <span className="ig-feature-meta">
                  Last changed by {change.by} · {new Date(change.at).toLocaleString()}
                </span>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={row.name}
              className={`ig-switch${on ? ' ig-switch--on' : ''}`}
              onClick={() => toggle(row)}
              disabled={!settings.loaded || savingFeature === row.feature}
            >
              <span className="ig-switch-knob" />
            </button>
          </div>
        );
      })}
      {error && <p className="ig-feature-error" role="alert">{error}</p>}
    </div>
  );
};

const IntegrationsPage = ({ user }) => {
  const [tab, setTab] = useState('keys');
  const token = localStorage.getItem('token');
  // Where the API is served — its own domain when the frontend is built with
  // REACT_APP_API_BASE_URL, otherwise the same site as the app.
  const baseUrl = API_BASE_URL || window.location.origin;

  if (!user?.isAdmin) {
    return <div className="ig-container"><p className="ig-denied">Integrations are available to administrators only.</p></div>;
  }

  return (
    <div className="ig-container">
      <div className="ig-header">
        <h1 className="ig-title">Integrations</h1>
        <p className="ig-subtitle">Connect other systems to Capturify with API keys.</p>
      </div>
      {user?.isSuperAdmin && <AppFeaturesCard />}
      <div className="ig-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'keys'} className={`ig-tab${tab === 'keys' ? ' ig-tab--active' : ''}`} onClick={() => setTab('keys')}>API keys</button>
        <button type="button" role="tab" aria-selected={tab === 'docs'} className={`ig-tab${tab === 'docs' ? ' ig-tab--active' : ''}`} onClick={() => setTab('docs')}>API documentation</button>
      </div>
      {tab === 'keys' ? <ApiKeysTab token={token} baseUrl={baseUrl} /> : <DocsTab token={token} baseUrl={baseUrl} />}
    </div>
  );
};

export default IntegrationsPage;
